// src/pages/GlSettingsPage.jsx

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Dialog, DialogContent, Typography, Box } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import chartOfAccountsApi from '../../api/chartOfAccountsApi';
import glSettingsApi from '../../api/glSettingsApi';
import SearchableSelect from '../../components/ui/SearchableSelect';

export default function GlSettingsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [accounts, setAccounts] = useState([]);
  
  const [settings, setSettings] = useState({
    salesAccountId: '', salesReturnAccountId: '', discountAllowedAccountId: '',
    purchasesAccountId: '', purchasesReturnAccountId: '', discountReceivedAccountId: '',
    inventoryAccountId: '', costOfGoodsSoldAccountId: '', inventoryAdjustmentAccountId: '',
    vatOutputAccountId: '', vatInputAccountId: '',
    cashShortageAccountId: '', cashOverageAccountId: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accRes, setRes] = await Promise.all([
        chartOfAccountsApi.getPostable(),
        glSettingsApi.get()
      ]);
      setAccounts(accRes.data || []);
      if (setRes.data) {
        const cleanSettings = {};
        Object.keys(settings).forEach(key => { cleanSettings[key] = setRes.data[key] || ''; });
        setSettings(cleanSettings);
      }
    } catch (error) {
      if (error.response?.status !== 404) toast.error("فشل تحميل البيانات");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(settings).map(([k, v]) => [k, v === '' ? null : v])
      );
      await glSettingsApi.update(payload);
      
      setSuccessModal(true);
      setTimeout(() => { setSuccessModal(false); }, 2000);
    } catch (error) {
      toast.error("فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const renderSelect = (label, name, filterFn = null) => (
    <div className="mb-4">
      <SearchableSelect
        label={label}
        name={name}
        options={filterFn ? accounts.filter(filterFn) : accounts}
        value={settings[name]}
        onChange={(val) => setSettings(prev => ({ ...prev, [name]: val }))}
        getOptionLabel={(o) => `${o.code} - ${o.nameAr}`}
        getOptionValue={(o) => o.id}
      />
    </div>
  );

  const groups = [
    {
      title: "المبيعات والعملاء",
      items: [
        { label: "حساب المبيعات", name: "salesAccountId", filter: a => a.accountType === 4 },
        { label: "حساب مردود المبيعات", name: "salesReturnAccountId", filter: a => [4, 5].includes(a.accountType) },
        { label: "حساب الخصم المسموح به", name: "discountAllowedAccountId", filter: a => [4, 5].includes(a.accountType) },
        { label: "حساب ضريبة المبيعات", name: "vatOutputAccountId", filter: a => a.accountType === 2 }
      ]
    },
    {
      title: "المشتريات والموردين",
      items: [
        { label: "حساب المشتريات/المخزون", name: "purchasesAccountId" },
        { label: "حساب مردود المشتريات", name: "purchasesReturnAccountId", filter: a => [1, 4, 5].includes(a.accountType) },
        { label: "حساب الخصم المكتسب", name: "discountReceivedAccountId", filter: a => [4, 5].includes(a.accountType) },
        { label: "حساب ضريبة المشتريات", name: "vatInputAccountId", filter: a => [1, 2].includes(a.accountType) }
      ]
    },
    {
      title: "المخزون وتكلفة البضاعة",
      items: [
        { label: "حساب المخزون الافتراضي", name: "inventoryAccountId", filter: a => a.accountType === 1 },
        { label: "حساب تكلفة البضاعة المباعة", name: "costOfGoodsSoldAccountId", filter: a => a.accountType === 5 },
        { label: "حساب تسوية المخزون", name: "inventoryAdjustmentAccountId", filter: a => a.accountType === 5 }
      ]
    },
    {
      title: "فروقات الصناديق",
      items: [
        { label: "حساب العجز النقدي", name: "cashShortageAccountId", filter: a => [1, 5].includes(a.accountType) },
        { label: "حساب الزيادة النقدية", name: "cashOverageAccountId", filter: a => [2, 4].includes(a.accountType) }
      ]
    }
  ];

  if (loading) return <div className="p-8 text-center">جاري التحميل...</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white rounded-3xl shadow-sm border border-gray-100 mt-6" dir="rtl">
      <div className="flex justify-between items-center mb-8 border-b pb-4">
        <h1 className="text-2xl font-bold text-gray-800">إعدادات الربط المحاسبي</h1>
        <button onClick={handleSubmit} disabled={saving} className="bg-blue-600 text-white px-8 py-2.5 rounded-xl hover:bg-blue-700 disabled:bg-gray-400 transition">
          {saving ? "جاري الحفظ..." : "حفظ التغييرات"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {groups.map((group, idx) => (
          <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <h2 className="text-lg font-bold text-blue-800 mb-5 border-b pb-3">{group.title}</h2>
            {group.items.map(item => renderSelect(item.label, item.name, item.filter))}
          </div>
        ))}
      </div>

      {/* نافذة النجاح */}
      <Dialog open={successModal} PaperProps={{ sx: { borderRadius: '24px', p: 3, textAlign: 'center' } }}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 60, color: 'success.main' }} />
            <Typography variant="h6" fontWeight="bold">تم الحفظ بنجاح!</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}